import { Dialog, LinearProgress } from '@mui/material'
import { useContext, useEffect, useState } from 'react'
import { Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  ensurePeriodicEmrBackgroundSync,
  startEmrBackgroundSync,
} from 'src/emr/database/emrBackgroundSync'
import { MentorProfileContext } from 'src/mentor/context/MentorProfileContext'

const SYNC_PROGRESS_SECTIONS = [
  { key: 'drugs', label: 'Drugs' },
  { key: 'complaints', label: 'Complaints' },
  { key: 'diagnosis', label: 'Diagnosis' },
  { key: 'observations', label: 'Observations' },
  { key: 'advices', label: 'Advices' },
  { key: 'instruction', label: 'Instructions' },
  { key: 'investigation', label: 'Investigation' },
  { key: 'patients', label: 'Patients' },
  { key: 'psychAssessments', label: 'Psych Assessments' },
  { key: 'genericDrugs', label: 'Generic Drugs' },
]

// EMR local-data sync. The actual sync engine lives in
// src/emr/database/emrBackgroundSync.js (single-flight, cross-tab locked, retrying,
// idle-scheduled); this component only triggers it:
// - takeActionsItself: starts the automatic periodic background sync
// - otherwise: renders the manual "Update Now" full-resync button with progress UI
export const ERMOptionsSyncComponent = ({ takeActionsItself, mentorClinicAssociationData }) => {
  const [dialogOpen, setDialogOpen] = useState(false)
  const [syncProgress, setSyncProgress] = useState({})
  const [isSyncing, setIsSyncing] = useState(false)

  // The auto-sync instance is mounted without props (MentorHeaderAndContent), so
  // fall back to the profile context — without this the background population has
  // no clinic brands to sync and silently does nothing.
  const profileContext = useContext(MentorProfileContext)
  const associationData =
    mentorClinicAssociationData || profileContext?.mentorClinicAssociationData

  const clinicBrandUniqueIds = [
    ...new Set((associationData || []).map((item) => item?.clinicBrandId).filter(Boolean)),
  ]

  // Helper function to shorten brand ID
  function formatClinicBrandId(id) {
    const foundClinic = (associationData || []).find((item) => item?.clinicBrandId === id)
    if (foundClinic?.clinicBrandName) {
      return foundClinic.clinicBrandName
    }
    return `clinic-${id && id.slice(-3)}`
  }

  function handleProgressUpdate(category, percent) {
    setSyncProgress((prev) => ({ ...prev, [category]: percent }))
  }

  async function handleClickForUpdate() {
    setDialogOpen(true)
    setIsSyncing(true)
    setSyncProgress({})
    try {
      const result = await startEmrBackgroundSync({
        clinicBrandIds: clinicBrandUniqueIds,
        force: true,
        waitForIdleFirst: false,
        onProgress: handleProgressUpdate,
      })
      if (result?.failures?.length > 0) {
        toast.error(
          `Sync finished with errors in: ${result.failures.map((item) => item.store).join(', ')}`,
        )
      } else if (result?.skippedRun) {
        toast.info('A sync is already running in another tab')
      } else {
        toast.success('Data Sync Completed')
      }
    } catch (error) {
      console.error('Sync failed', error)
      toast.error('Data sync failed — please try again')
    } finally {
      setIsSyncing(false)
      setSyncProgress({})
      setDialogOpen(false)
    }
  }

  // Automatic background population: starts once the browser is idle, re-syncs
  // periodically, never runs twice (single-flight per tab + Web Locks across tabs).
  const clinicBrandIdsKey = clinicBrandUniqueIds.join(',')
  useEffect(() => {
    if (takeActionsItself && clinicBrandUniqueIds.length > 0) {
      ensurePeriodicEmrBackgroundSync({ clinicBrandIds: clinicBrandUniqueIds })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takeActionsItself, clinicBrandIdsKey])

  return (
    <div>
      {!takeActionsItself && (
        <div>
          <div style={{ marginBottom: '16px' }}>
            <b>Update Local Data Options (Drugs/Diagnosis/etc.)</b>
          </div>
          <button className="btn btn-primary" onClick={handleClickForUpdate} disabled={isSyncing}>
            Update Now
          </button>
        </div>
      )}
      {dialogOpen && (
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <div
            style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
              minWidth: '400px',
            }}
          >
            <div style={{ padding: '16px 0 48px' }}>Syncing Data...</div>

            <div>
              {clinicBrandUniqueIds.map((id) => (
                <div key={id} style={{ marginBottom: '24px' }}>
                  <h4>{formatClinicBrandId(id)}</h4>
                  {SYNC_PROGRESS_SECTIONS.map((section) => (
                    <p key={section.key}>
                      {section.label} Sync Progress: {syncProgress[`${section.key}-${id}`] || 0}%
                      <LinearProgress
                        variant="determinate"
                        value={syncProgress[`${section.key}-${id}`] || 0}
                      />
                    </p>
                  ))}
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
              <button
                style={{
                  background: '#187ff2',
                  borderRadius: '24px',
                  padding: '8px 16px',
                  color: '#fff',
                }}
                disabled={isSyncing}
              >
                {isSyncing ? ' Saving ' : ' Save '}
                {isSyncing && <Spinner animation="border" variant="light" size="sm" />}
              </button>
            </div>
          </div>
          <button
            style={{
              background: 'linear-gradient(145deg, #6e7dff, #1e3bb1)',
              color: '#fff',
              padding: '8px 16px',
              borderRadius: '24px',
              width: 'fit-content',
              margin: '0 auto 24px',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
            }}
            onClick={() => setDialogOpen(false)}
          >
            Sync in background
          </button>
        </Dialog>
      )}
    </div>
  )
}
